import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, LogOut, Plus, ShieldCheck } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { getSupabaseClient, hasSupabaseConfig } from '../lib/supabaseClient';
import { upsertOrganizationLocal, upsertProfileLocal } from '../db/sync/membersSync';
import { buildDisplayName, ensureSelfOnOrgRoster } from '../db/sync/rosterSync';
import { writeCachedOrgContext, useMeetingLiveStore } from '../store/meetingLiveStore';
import { writeActiveOrganizationId } from '../auth/moduleAccess';
import SignedInUserLine from '../components/common/SignedInUserLine';

export default function OrgPickerPage() {
  const navigate = useNavigate();
  const { memberships, switchOrganization, signOut, ready, user, refreshMemberships, isPlatformAdmin } =
    useAuth();
  const [orgName, setOrgName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ready) return;
    if (!hasSupabaseConfig()) {
      navigate('/', { replace: true });
      return;
    }
    if (memberships.length === 1) {
      void switchOrganization(memberships[0].organization_id).then(() => {
        navigate(`/orgs/${memberships[0].organization_id}`, { replace: true });
      });
    }
  }, [ready, memberships, navigate, switchOrganization]);

  const onSelect = async (orgId) => {
    await switchOrganization(orgId);
    navigate(`/orgs/${orgId}`);
  };

  const onCreateOrg = async (e) => {
    e.preventDefault();
    if (!orgName.trim()) {
      setError('Organization name is required');
      return;
    }
    const meta = user?.user_metadata || {};
    const first = firstName.trim() || String(meta.first_name || '').trim();
    const last = lastName.trim() || String(meta.last_name || '').trim();
    if (!first || !last) {
      setError('First and last name are required');
      return;
    }
    const display = buildDisplayName(first, last);
    const supabase = getSupabaseClient();
    if (!supabase || !user) {
      setError('You must be signed in to create an organization');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await supabase.from('profiles').upsert({
        id: user.id,
        first_name: first,
        last_name: last,
        display_name: display,
      });
      const { data: orgId, error: rpcErr } = await supabase.rpc('signup_create_org_and_profile', {
        org_name: orgName.trim(),
        officer_role: 'Admin',
      });
      if (rpcErr) throw rpcErr;

      const { data: orgRow, error: orgFetchErr } = await supabase
        .from('organizations')
        .select('id, name, quorum_threshold')
        .eq('id', orgId)
        .single();
      if (orgFetchErr || !orgRow) throw orgFetchErr ?? new Error('Could not load organization');

      await upsertOrganizationLocal({
        id: orgRow.id,
        name: orgRow.name,
        quorum_threshold: orgRow.quorum_threshold ?? 50,
      });
      await upsertProfileLocal({ id: user.id, display_name: display });
      await ensureSelfOnOrgRoster(orgRow.id, first, last);
      useMeetingLiveStore.getState().setOrganizationContext(orgRow.id, orgRow.name);
      writeCachedOrgContext({
        organizationId: orgRow.id,
        organizationName: orgRow.name,
        userId: user.id,
      });
      writeActiveOrganizationId(orgRow.id);
      await refreshMemberships();
      navigate(`/orgs/${orgRow.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create organization');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-pp-paper px-4 py-12 text-pp-ink">
      <div className="w-full max-w-lg rounded-2xl border border-pp-line/80 bg-pp-surface p-8 shadow-pp">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Your organizations</h1>
            <p className="mt-1 text-sm text-pp-muted">Choose where you want to work</p>
            <SignedInUserLine className="mt-1" />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {isPlatformAdmin ? (
              <Link
                to="/platform"
                className="inline-flex items-center gap-1.5 rounded-lg border border-pp-accent/30 bg-pp-accentSoft px-2.5 py-1.5 text-xs font-semibold text-pp-accent hover:border-pp-accent/60"
              >
                <ShieldCheck className="h-3.5 w-3.5" />
                Platform
              </Link>
            ) : null}
            <button
              type="button"
              onClick={() => void signOut()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-pp-line px-2.5 py-1.5 text-xs font-medium text-pp-muted hover:bg-pp-paper"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        </div>

        {memberships.length === 0 ? (
          <div className="mt-8 space-y-4">
            <Building2 className="mx-auto h-10 w-10 text-pp-muted/60" />
            <p className="text-center text-sm text-pp-muted">
              You are not a member of any organization yet. Create one, or wait for an invite from
              your admin.
            </p>
            <form className="space-y-3" onSubmit={onCreateOrg}>
              <div>
                <label className="text-xs font-medium text-pp-muted">Organization name</label>
                <input
                  type="text"
                  required
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
                  placeholder="e.g. Northside Student Senate"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-pp-muted">First name</label>
                  <input
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-pp-muted">Last name</label>
                  <input
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
                    autoComplete="family-name"
                  />
                </div>
              </div>
              {error && <p className="text-sm text-rose-600">{error}</p>}
              <button
                type="submit"
                disabled={busy}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-pp-accent px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
                {busy ? 'Creating…' : 'Create organization'}
              </button>
            </form>
          </div>
        ) : (
          <ul className="mt-6 space-y-2">
            {memberships.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  onClick={() => void onSelect(m.organization_id)}
                  className="flex w-full items-center justify-between rounded-xl border border-pp-line bg-pp-paper/40 px-4 py-3 text-left transition hover:border-pp-accent/40 hover:bg-pp-paper"
                >
                  <div>
                    <p className="font-medium text-pp-ink">
                      {m.organization_name || 'Organization'}
                    </p>
                    <p className="text-xs text-pp-muted">{m.role}</p>
                  </div>
                  <span className="text-xs font-medium text-pp-accent">Open →</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
